import { ref } from 'vue';
import { defineStore } from 'pinia';
import type { Notification } from '@talkvibe/shared';

import { notificationService } from '../services/notification.ts';

/**
 * Notifications and the unread badge.
 *
 * Notifications now arrive over the user channel, so the badge is live rather
 * than up to a minute stale. The poll stays as a safety net at a much longer
 * interval: it costs one small request every five minutes and it is what
 * corrects the count if the socket is down, a frame was missed, or another
 * device read something.
 */
const POLL_INTERVAL_MS = 5 * 60_000;

export const useNotificationStore = defineStore('notification', () => {
  const items = ref<Notification[]>([]);
  const cursor = ref<string | null>(null);
  const loading = ref(false);
  const unreadCount = ref(0);

  let pollTimer: number | null = null;

  async function load(): Promise<void> {
    loading.value = true;
    try {
      const page = await notificationService.list();
      items.value = page.items;
      cursor.value = page.nextCursor;
    } finally {
      loading.value = false;
    }
  }

  async function loadMore(): Promise<void> {
    if (!cursor.value || loading.value) return;
    loading.value = true;
    try {
      const page = await notificationService.list(cursor.value);
      items.value = [...items.value, ...page.items];
      cursor.value = page.nextCursor;
    } finally {
      loading.value = false;
    }
  }

  async function refreshCount(): Promise<void> {
    try {
      const summary = await notificationService.summary();
      unreadCount.value = summary.unreadCount;
    } catch {
      // A failed badge refresh is not worth interrupting the user for.
    }
  }

  /** Applies an authoritative count, e.g. the one sent when the socket opens. */
  function setUnreadCount(count: number): void {
    unreadCount.value = Math.max(0, count);
  }

  /**
   * A notification pushed over the user channel.
   *
   * Prepended only when the list has already been loaded — inserting into a
   * list nobody has opened would leave a single item that looks like the whole
   * history. The count is set regardless, because that is what the badge shows.
   */
  function receive(notification: Notification, count: number): void {
    setUnreadCount(count);
    if (items.value.length === 0) return;
    // A reconnect can redeliver; the id is what makes this idempotent.
    if (items.value.some((entry) => entry.id === notification.id)) return;
    items.value = [notification, ...items.value];
  }

  async function markRead(id: string): Promise<void> {
    const target = items.value.find((entry) => entry.id === id);
    if (!target || target.isRead) return;

    // Update first, reconcile after: the badge should feel instant.
    items.value = items.value.map((entry) =>
      entry.id === id ? { ...entry, isRead: true } : entry,
    );
    unreadCount.value = Math.max(0, unreadCount.value - 1);

    try {
      await notificationService.markRead(id);
    } catch {
      items.value = items.value.map((entry) =>
        entry.id === id ? { ...entry, isRead: false } : entry,
      );
      await refreshCount();
    }
  }

  async function markAllRead(): Promise<void> {
    const previous = items.value;
    items.value = items.value.map((entry) => ({ ...entry, isRead: true }));
    unreadCount.value = 0;
    try {
      await notificationService.markAllRead();
    } catch {
      items.value = previous;
      await refreshCount();
    }
  }

  function startPolling(): void {
    if (pollTimer !== null) return;
    void refreshCount();
    pollTimer = window.setInterval(() => void refreshCount(), POLL_INTERVAL_MS);
  }

  function stopPolling(): void {
    if (pollTimer === null) return;
    window.clearInterval(pollTimer);
    pollTimer = null;
  }

  function reset(): void {
    stopPolling();
    items.value = [];
    cursor.value = null;
    unreadCount.value = 0;
  }

  return {
    items,
    cursor,
    loading,
    unreadCount,
    load,
    loadMore,
    refreshCount,
    setUnreadCount,
    receive,
    markRead,
    markAllRead,
    startPolling,
    stopPolling,
    reset,
  };
});
